"use client"

import { useHoverContext } from "@/contexts/HoverContext"
import { useEffect, useRef, useState } from "react"
import { gsap } from "gsap"
import { IconClick, IconHandMove, IconHome, IconLink, IconMenu, IconSparkles, IconX } from "@tabler/icons-react"

export default function Cursor() {
  const { hoverTarget } = useHoverContext()
  const circle = useRef<HTMLDivElement>(null)
  const mouse = useRef({ x: 0, y: 0 })
  const delayedMouse = useRef({ x: 0, y: 0 })

  // Control icon rendering state
  const [showIcon, setShowIcon] = useState(false)

  // Smooth follow animation
  useEffect(() => {
    const lerp = (x: number, y: number, a: number) => x * (1 - a) + y * a

    const moveCircle = (x: number, y: number) => {
      if (!circle.current) return
      gsap.set(circle.current, {
        x,
        y,
        xPercent: -50,
        yPercent: -50
      })
    }

    const manageMouseMove = (e: MouseEvent) => {
      mouse.current = {
        x: e.clientX,
        y: e.clientY
      }
    }

    const animate = () => {
      delayedMouse.current = {
        x: lerp(delayedMouse.current.x, mouse.current.x, 0.1),
        y: lerp(delayedMouse.current.y, mouse.current.y, 0.1)
      }
      moveCircle(delayedMouse.current.x, delayedMouse.current.y)
      requestAnimationFrame(animate)
    }

    animate()
    window.addEventListener("mousemove", manageMouseMove)
    return () => window.removeEventListener("mousemove", manageMouseMove)
  }, [])

  // Animate cursor styles and control icon visibility
  useEffect(() => {
    if (!circle.current) return

    if (hoverTarget) {
      setShowIcon(false) // Temporarily hide icon during transition
      gsap.to(circle.current, {
        width: 120,
        height: 120,
        backgroundColor: "rgba(255, 255, 255, 0.85)",
        opacity: 0.95,
        filter: "invert(1)",
        duration: 0.3,
        ease: "power2.out",
        onComplete: () => setShowIcon(true)
      })
    } else {
      setShowIcon(false)
      gsap.to(circle.current, {
        width: 50,
        height: 50,
        backgroundColor: "transparent",
        opacity: 1,
        filter: "none",
        duration: 0.3,
        ease: "power2.out"
      })
    }
  }, [hoverTarget])

  const renderIcon = () => {
    switch (hoverTarget) {
      case "work":
        return <IconSparkles stroke={1} />
      case "drag":
        return <IconHandMove stroke={1} />
      case "menu-open":
        return <IconMenu stroke={1} />
      case "menu-close":
        return <IconX stroke={1} />
      case "logo":
        return <IconHome stroke={1} />
      case "link":
        return <IconLink stroke={1} />
      case "button":
        return <IconClick stroke={1} />
      default:
        return null
    }
  }

  return (
    <div ref={circle} className={`top-0 left-0 fixed rounded-full border-2 pointer-events-none flex items-center justify-center transition-colors ${hoverTarget ? "z-50" : "mix-blend-difference z-[2]"}`}>
      {hoverTarget && showIcon && renderIcon()}
    </div>
  )
}
